/**
 * React Query hooks for alerts data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listAlerts,
  getAlert,
  createAlert,
  updateAlert,
  deleteAlert,
  buildQuery,
  testQuery,
  type AlertFilters,
  type AlertCreateData,
  type AlertUpdateData,
  type BuildQueryResponse,
  type TestQueryResponse,
  type PaginatedResponse,
} from "@/services/alertsService";
import { useApiReady } from "@/hooks/useApi";
import type { Alert, AlertQuery } from "@/types/alerts";

/**
 * Hook to fetch a paginated list of alerts.
 */
export function useAlerts(filters?: AlertFilters) {
  const isApiReady = useApiReady();

  return useQuery<PaginatedResponse<Alert>, Error>({
    queryKey: ["alerts", filters],
    queryFn: () => listAlerts(filters),
    staleTime: 30000, // 30 seconds
    enabled: isApiReady,
  });
}

/**
 * Hook to fetch a single alert by ID.
 */
export function useAlert(id: number) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["alerts", id],
    queryFn: () => getAlert(id),
    staleTime: 30000, // 30 seconds
    enabled: isApiReady && !!id,
  });
}

/**
 * Hook to create a new alert.
 */
export function useCreateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AlertCreateData) => createAlert(data),
    onSuccess: async () => {
      // Invalidate alerts list to refetch
      await queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

/**
 * Hook to update an alert.
 */
export function useUpdateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AlertUpdateData }) => updateAlert(id, data),
    onSuccess: async (_, variables) => {
      // Invalidate both the list and the specific alert
      await queryClient.invalidateQueries({ queryKey: ["alerts"] });
      await queryClient.invalidateQueries({ queryKey: ["alerts", variables.id] });
    },
  });
}

/**
 * Hook to delete an alert.
 */
export function useDeleteAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteAlert(id),
    onSuccess: async () => {
      // Invalidate alerts list to refetch
      await queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

/**
 * Hook to build a query from natural language.
 */
export function useBuildQuery() {
  return useMutation<BuildQueryResponse, Error, { naturalLanguage: string; includeExamples?: boolean }>({
    mutationFn: ({ naturalLanguage, includeExamples = true }) =>
      buildQuery(naturalLanguage, includeExamples),
  });
}

/**
 * Hook to test a query against the database.
 */
export function useTestQuery() {
  return useMutation<
    TestQueryResponse,
    Error,
    { query: AlertQuery; timeWindowHours?: number; sampleLimit?: number }
  >({
    mutationFn: ({ query, timeWindowHours = 24, sampleLimit = 10 }) =>
      testQuery(query, timeWindowHours, sampleLimit),
  });
}
